// Marker anatomy tokens — ClickUp ticket 2 ("Marker anatomy: fill, icon, ring and state badges"),
// list "Web: Map markers & route lines". Values lifted from stippl-knowledge/design/map-markers/
// (markers.md, states.md, icons.md, component-values.md). Pure data + resolvers only — MarkerAnatomy.tsx
// is the component that renders them. Not wired into the live map yet.
import type { MarkerIconName } from "./markers";

// ---------------------------------------------------------------------------------------------
// POI categories (icons.md) — colour comes from the category, the drawing from the type.
// ---------------------------------------------------------------------------------------------

export type PoiCategory = "hotels" | "food-drink" | "things-to-do" | "outdoors";

export interface MarkerColors {
	/** light-tint fill, the only one in scope for now (see README.md — dark polarity is future work) */
	fill: string;
	/** the per-type ink column in markers.md — kept as data for the alternative, NOT the agreed choice */
	iconInk: string;
}

export const POI_CATEGORY_COLORS: Record<PoiCategory, MarkerColors> = {
	hotels: { fill: "#d6f0e2", iconInk: "#0b7a58" },
	"food-drink": { fill: "#e5daf8", iconInk: "#5b32b8" },
	"things-to-do": { fill: "#fbe7bf", iconInk: "#8a5a12" },
	outdoors: { fill: "#cfe6f8", iconInk: "#1a6ba3" },
};

// Ticket 4 ("Zoom ladder"), markers.md "Dots when zoomed out": a dot keeps its type colour, but uses
// the saturated variant of the fill, not the light tint — a dot has no ring/shape to carry the colour
// on top of, so it can afford to be as vivid as it needs to be. Keyed by the light fill so a caller
// that already resolved a POI_CATEGORY_COLORS fill (resolvePlaceMarkerVisual.ts) can look the dot
// colour up directly, the same indirection prototype.html's own DOTMAP uses.
// Also covers destination dots (prototype.html's DESTDOT, keyed there by type name instead of fill —
// city/town/region/island-beach happen to share their fill with a POI category so those 4 entries
// above already answer for them; national-park/natural-feature don't share a fill with any POI
// category, so they get their own two entries here — same table, same "look up the saturated dot
// colour by light fill" principle, no forward reference to DESTINATION_TYPE_TABLE needed since the
// hex values are fixed).
export const POI_DOT_COLOR_BY_FILL: Record<string, string> = {
	[POI_CATEGORY_COLORS.hotels.fill]: "#12b981",
	[POI_CATEGORY_COLORS["food-drink"].fill]: "#8b5cf6",
	[POI_CATEGORY_COLORS["things-to-do"].fill]: "#eaa506",
	[POI_CATEGORY_COLORS.outdoors.fill]: "#22a3f0",
	"#dcf0c8": "#3fb81b", // national-park (DESTINATION_TYPE_TABLE)
	"#d9f1ea": "#0eb3cc", // natural-feature (DESTINATION_TYPE_TABLE)
};

// The original 26 app marker types the map code knew (icons.md). `city` and `destination` are
// deliberately absent here — both take their colour/icon from DESTINATION_TYPE_TABLE instead, see
// resolveMarkerVisual().
//
// Extended past those 26: getPlaceIcon()/resolvePlaceIcon() (utils/placeTypes/typeIconMap.json) can
// resolve a place to any of ~230 fine icon slugs, not just this table's original keys — a "restaurant"
// place resolved to iconName "restaurant" found no row here and silently kept the old flat icon on the
// map marker. The rows below were added by cross-referencing typeIconMap.json's real `icon` output
// (which of the ~230 slugs production data actually resolves to, and how often) plus every slug
// IconPicker lets a user hand-pick (constants/place.ts ICON_CATEGORIES), against the 24 ticket-1 marker
// drawings that exist — there is no chunky drawing per fine icon, only one per rough category, so
// several slugs intentionally collapse onto the same marker glyph (every cuisine sub-type onto
// "eat-marker", every alcohol-adjacent one onto "cocktail-marker", etc). Entries with no clear visual
// match (wellness, business, photography, transport-bucket icons — plane/train/bus/rentalcar/...) were
// left out rather than forced onto an unrelated glyph; those places keep the old flat icon on the marker.
export const MARKER_TYPE_TABLE: Record<string, { category: PoiCategory; icon: MarkerIconName }> = {
	// hotels — bed-marker--chunky
	apartment: { category: "hotels", icon: "bed-marker--chunky" },
	bb: { category: "hotels", icon: "bed-marker--chunky" },
	"bed&breakfast": { category: "hotels", icon: "bed-marker--chunky" },
	bed: { category: "hotels", icon: "bed-marker--chunky" },
	hostel: { category: "hotels", icon: "bed-marker--chunky" },
	hotel: { category: "hotels", icon: "bed-marker--chunky" },
	resort: { category: "hotels", icon: "bed-marker--chunky" },
	villa: { category: "hotels", icon: "bed-marker--chunky" },
	sleep: { category: "hotels", icon: "bed-marker--chunky" },
	// hotels — tent-marker--chunky (camp-style stays)
	campground: { category: "hotels", icon: "tent-marker--chunky" },
	camping: { category: "hotels", icon: "tent-marker--chunky" },
	campervan: { category: "hotels", icon: "tent-marker--chunky" },
	// food-drink — breakfast-marker--chunky
	breakfast: { category: "food-drink", icon: "breakfast-marker--chunky" },
	brunch: { category: "food-drink", icon: "breakfast-marker--chunky" },
	// food-drink — coffee-marker--chunky
	coffee: { category: "food-drink", icon: "coffee-marker--chunky" },
	// food-drink — eat-marker--chunky (every cuisine/meal sub-type collapses here, no per-cuisine glyph exists)
	cuisine: { category: "food-drink", icon: "eat-marker--chunky" },
	dinner: { category: "food-drink", icon: "eat-marker--chunky" },
	lunch: { category: "food-drink", icon: "eat-marker--chunky" },
	eatdrink: { category: "food-drink", icon: "eat-marker--chunky" },
	restaurant: { category: "food-drink", icon: "eat-marker--chunky" },
	asian: { category: "food-drink", icon: "eat-marker--chunky" },
	italian: { category: "food-drink", icon: "eat-marker--chunky" },
	mexican: { category: "food-drink", icon: "eat-marker--chunky" },
	seafood: { category: "food-drink", icon: "eat-marker--chunky" },
	vegan: { category: "food-drink", icon: "eat-marker--chunky" },
	bbq: { category: "food-drink", icon: "eat-marker--chunky" },
	fastfood: { category: "food-drink", icon: "eat-marker--chunky" },
	finedining: { category: "food-drink", icon: "eat-marker--chunky" },
	// food-drink — cocktail-marker--chunky (bars/alcohol/nightlife)
	nightlife: { category: "food-drink", icon: "cocktail-marker--chunky" },
	cocktail: { category: "food-drink", icon: "cocktail-marker--chunky" },
	beer: { category: "food-drink", icon: "cocktail-marker--chunky" },
	wine: { category: "food-drink", icon: "cocktail-marker--chunky" },
	club: { category: "food-drink", icon: "cocktail-marker--chunky" },
	// food-drink — sweets-marker--chunky
	sweets: { category: "food-drink", icon: "sweets-marker--chunky" },
	bakery: { category: "food-drink", icon: "sweets-marker--chunky" },
	icecream: { category: "food-drink", icon: "sweets-marker--chunky" },
	// things-to-do — museum-marker--chunky (culture/history)
	culture: { category: "things-to-do", icon: "museum-marker--chunky" },
	museum: { category: "things-to-do", icon: "museum-marker--chunky" },
	historic: { category: "things-to-do", icon: "museum-marker--chunky" },
	theatre: { category: "things-to-do", icon: "museum-marker--chunky" },
	heritageSite: { category: "things-to-do", icon: "museum-marker--chunky" },
	arts: { category: "things-to-do", icon: "museum-marker--chunky" },
	// things-to-do — star-marker--chunky (generic "attraction", no dedicated glyph)
	landmarks: { category: "things-to-do", icon: "star-marker--chunky" },
	landmark: { category: "things-to-do", icon: "star-marker--chunky" },
	seedo: { category: "things-to-do", icon: "star-marker--chunky" },
	"see&do": { category: "things-to-do", icon: "star-marker--chunky" },
	entertainment: { category: "things-to-do", icon: "star-marker--chunky" },
	adventure: { category: "things-to-do", icon: "star-marker--chunky" },
	// things-to-do — shopping-marker--chunky
	shopping: { category: "things-to-do", icon: "shopping-marker--chunky" },
	supermarket: { category: "things-to-do", icon: "shopping-marker--chunky" },
	clothing: { category: "things-to-do", icon: "shopping-marker--chunky" },
	makeup: { category: "things-to-do", icon: "shopping-marker--chunky" },
	gift: { category: "things-to-do", icon: "shopping-marker--chunky" },
	// things-to-do — sports-marker--chunky
	sports: { category: "things-to-do", icon: "sports-marker--chunky" },
	gym: { category: "things-to-do", icon: "sports-marker--chunky" },
	bicycle: { category: "things-to-do", icon: "sports-marker--chunky" },
	// things-to-do — ticket-marker--chunky (tours/ticketed events)
	// tour/tours are duplicates in the app today — ticket 1 collapses them onto the same drawing.
	tours: { category: "things-to-do", icon: "ticket-marker--chunky" },
	tour: { category: "things-to-do", icon: "ticket-marker--chunky" },
	festival: { category: "things-to-do", icon: "ticket-marker--chunky" },
	musicfestival: { category: "things-to-do", icon: "ticket-marker--chunky" },
	// outdoors — island-marker--chunky (beach/water)
	beach: { category: "outdoors", icon: "island-marker--chunky" },
	island: { category: "outdoors", icon: "island-marker--chunky" },
	swimming: { category: "outdoors", icon: "island-marker--chunky" },
	watersports: { category: "outdoors", icon: "island-marker--chunky" },
	kitesurfing: { category: "outdoors", icon: "island-marker--chunky" },
	// outdoors — mountain-marker--chunky (nature/land)
	nature: { category: "outdoors", icon: "mountain-marker--chunky" },
	wildlife: { category: "outdoors", icon: "mountain-marker--chunky" },
	naturalFeature: { category: "outdoors", icon: "mountain-marker--chunky" },
	mountainrange: { category: "outdoors", icon: "mountain-marker--chunky" },
	scenic: { category: "outdoors", icon: "mountain-marker--chunky" },
	park: { category: "outdoors", icon: "mountain-marker--chunky" },
	hike: { category: "outdoors", icon: "mountain-marker--chunky" },
	walk: { category: "outdoors", icon: "mountain-marker--chunky" },
	trekkin: { category: "outdoors", icon: "mountain-marker--chunky" },
};

// ---------------------------------------------------------------------------------------------
// Destination types (markers.md) — used for `type === "city"` and for `type === "destination"`
// (which takes its icon from the destination's own type: city, town, region, park, island, nature).
// ---------------------------------------------------------------------------------------------

export type DestinationType = "city" | "town" | "region" | "national-park" | "island-beach" | "natural-feature" | "heritage-site" | "ski-resort";

// A destination marker whose type nothing has resolved yet (an AI-suggested destination, or a
// destination saved to ideas) still gets a type icon, defaulting to "region" rather than falling back
// to the numbered look: that number identifies a confirmed stop's place in the route, which neither
// of those is part of.
export const DEFAULT_DESTINATION_TYPE: DestinationType = "region";

export interface DestinationColors extends MarkerColors {
	/** deep fill for dark basemaps — not consumed by MarkerAnatomy yet, only light styles are in scope */
	deepFill: string;
}

// heritage-site/ski-resort added 2026-09 to close the gap resolveExploreDestinationType.ts used to
// fall through on (explore's dataset carries these two types, markers.md's original 6 didn't cover
// them). Fill/ink are provisional — sienna for heritage-site (unused hue among the existing 6),
// slate ice-blue for ski-resort (cooler/less saturated than island-beach's sky blue so the two don't
// read the same at a glance) — pending design sign-off, same as the other six.
export const DESTINATION_TYPE_TABLE: Record<DestinationType, DestinationColors & { icon: MarkerIconName }> = {
	city: { fill: "#d6f0e2", deepFill: "#0d8762", iconInk: "#0b7a58", icon: "city-marker--chunky" },
	town: { fill: "#fbe7bf", deepFill: "#a56a08", iconInk: "#8a5a12", icon: "town-marker--chunky" },
	region: { fill: "#e5daf8", deepFill: "#5b32b8", iconInk: "#5b32b8", icon: "region-marker--chunky" },
	"national-park": { fill: "#dcf0c8", deepFill: "#3a7a28", iconInk: "#3a7a28", icon: "tree-marker--chunky" },
	"island-beach": { fill: "#cfe6f8", deepFill: "#17618f", iconInk: "#1a6ba3", icon: "island-marker--chunky" },
	"natural-feature": { fill: "#d9f1ea", deepFill: "#0f5f6b", iconInk: "#0f5f6b", icon: "mountain-marker--chunky" },
	"heritage-site": { fill: "#f5ddd0", deepFill: "#a15c33", iconInk: "#8a4a28", icon: "heritage-site-marker--chunky" },
	"ski-resort": { fill: "#e6edf5", deepFill: "#3c5875", iconInk: "#3c5875", icon: "ski-resort-marker--chunky" },
};

/** The agreed icon colour on a light fill (markers.md). The per-type ink column is the alternative kept in the prototype. */
export const MARKER_ICON_COLOR_AGREED = "#14202a";

export interface MarkerVisual {
	fill: string;
	iconInk: string;
	icon: MarkerIconName;
}

/**
 * Resolve fill + icon for one of the 26 app marker types.
 * `type === "destination"` needs `destinationType` (city/town/region/national-park/island-beach/natural-feature);
 * without it there is nothing correct to draw, so it returns undefined rather than guessing.
 * `type === "city"` resolves to the city destination type directly (icons.md: same hex, same icon).
 */
export function resolveMarkerVisual(type: string, destinationType?: DestinationType): MarkerVisual | undefined {
	if (type === "destination") return destinationType ? resolveDestinationVisual(destinationType) : undefined;
	if (type === "city") return resolveDestinationVisual("city");
	const entry = MARKER_TYPE_TABLE[type];
	if (!entry) return undefined;
	const { fill, iconInk } = POI_CATEGORY_COLORS[entry.category];
	return { fill, iconInk, icon: entry.icon };
}

export function resolveDestinationVisual(destinationType: DestinationType): MarkerVisual {
	const { fill, iconInk, icon } = DESTINATION_TYPE_TABLE[destinationType];
	return { fill, iconInk, icon };
}

// Canonical mapping from the explore dataset's `type` string (models/ExploreDestination.ts: "City" /
// "Town" / "Island" / "Region" / "National Park" / "Natural Feature" / "Heritage Site" / "Ski
// Resort") onto DestinationType. Single source of truth for both resolveExploreDestinationType.ts
// (trip map, matches by name+distance) and the explore world/country map's own marker rendering
// (ExploreDestinationMarker.tsx/exploreMapConfig.ts) — two hand-maintained copies of this list is exactly how
// heritage-site/ski-resort fell through with no marker design before 2026-09.
export const EXPLORE_TYPE_TO_DESTINATION_TYPE: Record<string, DestinationType> = {
	City: "city",
	Town: "town",
	Region: "region",
	"National Park": "national-park",
	Island: "island-beach",
	"Natural Feature": "natural-feature",
	"Heritage Site": "heritage-site",
	"Ski Resort": "ski-resort",
};

// ---------------------------------------------------------------------------------------------
// zoom ladder radii, px (markers.md).
// ---------------------------------------------------------------------------------------------

export type MarkerKind = "poi" | "destination" | "dot";

// ---------------------------------------------------------------------------------------------
// Zoom thresholds for the ladder (markers.md ticket 4), named here so the handover numbers live in
// one place instead of as literals spread across AttractionIcon.tsx and MapContents.tsx.
//
// Revised by design review 2026-09 (Robin, against the PR #1195 preview build): POIs were arriving
// far too late — nothing at all on screen below z9.5, so a country or region view showed only the
// trip's own stops and read as an empty map. Dots now start ~3 stops earlier so there is always a
// hint that something is there, and the full marker follows at 10.5 rather than 11.9.
// ---------------------------------------------------------------------------------------------

/** Below this zoom a destination collapses to a bare dot. Disabled by the 2026-09 review — a
 *  numbered stop's number shrank to ~7px and a suggested destination lost its icon entirely.
 *  Set a real zoom (the old value was 5.2) to bring the collapse back. */
export const DESTINATION_DOT_MAX_ZOOM = Number.NEGATIVE_INFINITY;
/** The stop photo card shows from this zoom. Was 6.6. */
export const STOP_PHOTO_IN_ZOOM = 5.5;
/** ...and collapses back to a plain marker here. Tracks POI_FULL_MARKER_ZOOM on purpose: the review
 *  asked for the photo to hand the map over exactly when POIs arrive as full markers, rather than
 *  sitting on top of a POI-dense view. Was 13.0. */
export const STOP_PHOTO_OUT_ZOOM = 9.0;
/** Below this zoom a POI is not drawn at all. Was 9.5. */
export const POI_HIDDEN_BELOW_ZOOM = 6.5;
/** At/above this zoom a POI is a full marker; between the two it is a coloured dot. Was 11.9, then
 *  10.5 — which left the dot stage running 4.0 zoom stops where it used to run 2.4. Bringing POIs in
 *  early is only worth it if they graduate to a real marker at a sane distance, so the dot stage is
 *  back to ~2.5 stops (6.5 -> 9.0). */
export const POI_FULL_MARKER_ZOOM = 9.0;
/** At/above this zoom a POI is *eligible* for a name plate — whether it actually gets one is then
 *  decided per label by the screen-space pass in MapContents.tsx (pickVisibleLabels.ts).
 *
 *  Was 12.6, then 11.5, then 10.0, then briefly 15.0. That 15.0 was a blunt stand-in while POI names
 *  were not thinned at all: Amsterdam city centre only read cleanly at ~z16, while a Hakone cluster
 *  was already crowded at ~z14 — one global number cannot serve both, because density is local and a
 *  zoom gate is not. With the thinning wired the gate only has to keep names off the map while the
 *  markers are still resolving, so it sits 3 stops above POI_FULL_MARKER_ZOOM. */
export const POI_NAME_PLATE_ZOOM = 12.0;
/** At/above this zoom Mapbox's own place labels are dimmed so ours read. Was 11.4, then 10.0; kept a
 *  touch below POI_FULL_MARKER_ZOOM, the same relationship the original 11.4/11.9 pair had. */
export const BASEMAP_DIM_ZOOM = 8.5;

export function getMarkerRadius(kind: MarkerKind, zoom: number): number {
	// Design review 2026-09 (Robin), second pass against the preview build: at one flat size the
	// destination markers crowded a whole-trip view — stops piled into dark clusters around Osaka and
	// Hakone — so the destination radius drops to 70% (23 -> 16).
	//
	// POI and dot are deliberately left at their ticket-2/4 sizes. The POI marker reads correctly at
	// R=17 and was not part of the complaint, and the dot is the "something is here" hint at the zooms
	// POIs now appear at — already the smallest thing on the map, so shrinking it would work against
	// the same review's reason for bringing POIs in earlier.
	if (kind === "dot") return 7;
	if (kind === "poi") return 17;
	// One size at every zoom: the old 15/19/23 step-up shrank a numbered stop at exactly the zooms
	// where the whole trip is on screen and the number is what identifies it. `zoom` stays in the
	// signature since callers pass it for every kind.
	return 16;
}

// ---------------------------------------------------------------------------------------------
// Ring + shadow (component-values.md). A selected marker swaps the shadow for "neon soft".
//
// Selected used to be a 3.5px ring in Stippl green (#13c892) on every marker. Against the map ground
// that green measures only 1.3 to 2.1 contrast, so the selected marker barely stood out from its
// neighbours. Mobile ships "neon soft" instead (design/screens/map-markers-mobile.html, NEON.zacht +
// selLook()) and web follows: a 2px rim and a 9px glow at 42% alpha, both in the marker's own
// saturated type colour, plus a soft drop shadow. The white border stays as the kind's normal border.
// Lift, float, ripple and ground dot are mobile-only motion and are not ported.
// ---------------------------------------------------------------------------------------------

export interface MarkerRing {
	border: string;
	boxShadow: string;
}

/** The selected colour of a numbered stop: its navy fill has no type colour of its own, so it takes Stippl green. */
export const STOP_SELECTED_COLOR = "#13c892";

/**
 * The saturated colour a selected marker lights up in, looked up from the marker's light fill. POIs and
 * the destination types that share a POI category's fill answer from POI_DOT_COLOR_BY_FILL. A
 * destination type with no saturated entry (heritage-site, ski-resort) uses its own `deepFill` rather
 * than a new hex. No fill (a numbered stop) or an unknown one falls back to STOP_SELECTED_COLOR.
 */
export function getSelectedColor(fill?: string): string {
	if (!fill) return STOP_SELECTED_COLOR;
	const saturated = POI_DOT_COLOR_BY_FILL[fill];
	if (saturated) return saturated;
	const destinationRow = Object.values(DESTINATION_TYPE_TABLE).find((row) => row.fill === fill);
	return destinationRow ? destinationRow.deepFill : STOP_SELECTED_COLOR;
}

/** `#rrggbb` (or `#rgb`) to its `r,g,b` channels. Only used for the neon glow, where the colour is always one of our own hex tokens. */
function hexToChannels(hex: string): string {
	const digits = hex.replace("#", "");
	const full = digits.length === 3 ? digits.replace(/./g, "$&$&") : digits;
	return [0, 2, 4].map((start) => parseInt(full.slice(start, start + 2), 16)).join(",");
}

/** The "neon soft" selected shadow: 2px rim + 9px glow at 42% in `color`, then a soft drop shadow. */
function getNeonSoftShadow(color: string): string {
	return `0 0 0 2px ${color}, 0 0 9px 0 rgba(${hexToChannels(color)},.42), 0 5px 10px rgba(15,62,74,.22)`;
}

export function getMarkerRing(kind: MarkerKind, active: boolean, selectedColor: string = STOP_SELECTED_COLOR): MarkerRing {
	if (active) {
		const border = kind === "destination" ? "2.5px solid #fff" : "2px solid #fff";
		return { border, boxShadow: getNeonSoftShadow(selectedColor) };
	}
	switch (kind) {
		case "destination":
			return { border: "2.5px solid #fff", boxShadow: "0 2px 7px rgba(15,62,74,.45)" };
		case "dot":
			return { border: "2px solid #fff", boxShadow: "0 1px 4px rgba(15,62,74,.45)" };
		case "poi":
		default:
			return { border: "2px solid #fff", boxShadow: "0 2px 7px rgba(15,62,74,.45)" };
	}
}

// ---------------------------------------------------------------------------------------------
// State badges (states.md, component-values.md).
//
// No separate "destination-suggestion" badge: an AI-suggested destination reuses "ai-suggestion",
// the same purple sparkle a suggested POI gets (AttractionIcon.tsx's destination branch), rather
// than inventing a distinct badge/design for it.
// ---------------------------------------------------------------------------------------------

export type MarkerBadge = "none" | "in-trip" | "ai-suggestion";
export type BadgeHost = "marker" | "photo-card";
export type BadgeGlyph = "check" | "sparkle";

export interface BadgeVisual {
	size: number;
	border: string;
	disc: string;
	glyph: BadgeGlyph;
	glyphIcon: MarkerIconName;
	glyphColor: string;
	glyphSize: number;
	/** px offsets for the corner overlay, both negative (badge sits partly outside the marker) */
	offsetTop: number;
	offsetRight: number;
}

const BADGE_DISC: Record<Exclude<MarkerBadge, "none">, string> = {
	"in-trip": "#0b7a58",
	"ai-suggestion": "#7c3aed",
};

const BADGE_GLYPH: Record<Exclude<MarkerBadge, "none">, { glyph: BadgeGlyph; icon: MarkerIconName }> = {
	"in-trip": { glyph: "check", icon: "check-marker--chunky" },
	"ai-suggestion": { glyph: "sparkle", icon: "ai-marker--chunky" },
};

// component-values.md / the ticket text give the base size as max(floor, round(radius * factor)) and
// stop there. The live prototype (prototype.html, "onCard" in its el() function) goes one step
// further for the photo-card host only: the badge actually drawn is 1.30x that base, and its glyph is
// sized off the BASE (not the enlarged badge) times an extra 1.16. Neither multiplier is written down
// anywhere in the ticket text — README.md is explicit that when the document and the prototype
// disagree the prototype wins, so these two constants come from reading its source, not the ticket.
const PHOTO_CARD_BADGE_SCALE = 1.3;
const PHOTO_CARD_GLYPH_SCALE = 1.16;

/** the base size before the photo-card-only 1.3x enlargement — also what the glyph fraction is taken of */
function getBadgeBaseSize(host: BadgeHost, radius: number): number {
	return host === "photo-card" ? Math.max(17, Math.round(radius * 1.0)) : Math.max(16, Math.round(radius * 0.78));
}

/** size = max(floor, round(radius * factor)) — the floor is the point, see component-values.md */
export function getBadgeSize(host: BadgeHost, radius: number): number {
	const base = getBadgeBaseSize(host, radius);
	return host === "photo-card" ? Math.round(base * PHOTO_CARD_BADGE_SCALE) : base;
}

/** check is 0.62 of the badge, sparkle 0.50 — do not unify them, the sparkle fills its viewBox more fully */
export function getBadgeGlyphSize(host: BadgeHost, radius: number, glyph: BadgeGlyph): number {
	const base = getBadgeBaseSize(host, radius);
	const fraction = glyph === "check" ? 0.62 : 0.5;
	if (host === "photo-card") return Math.max(10, Math.round(base * fraction * PHOTO_CARD_GLYPH_SCALE));
	return Math.max(9, Math.round(base * fraction));
}

export function getBadgeVisual(badge: Exclude<MarkerBadge, "none">, host: BadgeHost, radius: number): BadgeVisual {
	const size = getBadgeSize(host, radius);
	const { glyph, icon } = BADGE_GLYPH[badge];
	const glyphSize = getBadgeGlyphSize(host, radius, glyph);
	const border = host === "photo-card" ? "2.5px solid #fff" : "2px solid #fff";
	const offset = host === "photo-card" ? 7 : Math.round(size * 0.28);
	return {
		size,
		border,
		disc: BADGE_DISC[badge],
		glyph,
		glyphIcon: icon,
		glyphColor: "#fff",
		glyphSize,
		offsetTop: -offset,
		offsetRight: -offset,
	};
}

// ---------------------------------------------------------------------------------------------
// Icon stroke weight (component-values.md, "agreed: thicker"). Applied via CSS in MarkerAnatomy.scss
// to every path drawn inside a marker's type icon; exported here only so the value has one source.
// ---------------------------------------------------------------------------------------------

export const MARKER_ICON_STROKE_WIDTH = 0.9;
export const MARKER_ICON_STROKE_WIDTH_OUTLINE = 2.7; // svg path[fill="none"]

/** icon size inside the circle, R * 1.05 (markers.md / component-values.md) */
export function getMarkerIconSize(radius: number): number {
	return Math.round(radius * 1.05);
}

// ---------------------------------------------------------------------------------------------
// Ordering (states.md): "Numbered stops render above other markers. Within one layer, a marker
// closer to the bottom of the viewport renders above one higher up... The active marker renders
// above everything, regardless of position."
//
// Deliberately tiny, fixed tiers — never a raw pixel/screen-position value — so a map marker's
// z-index can never climb high enough to compete with unrelated dashboard chrome (modals, dropdowns,
// nav) that also relies on z-index. "Within one layer" (POI vs POI) is achieved by the CALLER sorting
// its marker list by ascending screen Y (map.project(...).y) before rendering — two elements at the
// same z-index stack in DOM/render order, so a marker painted later (further down the sorted list)
// paints on top of one painted earlier, with no numeric encoding of position needed. A destination's
// base tier (1) sits strictly above a hovered POI's tier (POI_MARKER_HOVER_Z_INDEX, 1) and ties are
// broken the same way — MapContents.tsx renders every destination after every POI block, so a
// same-tier tie still resolves to the destination on top.
// ---------------------------------------------------------------------------------------------

export const POI_MARKER_Z_INDEX = 0;
export const POI_MARKER_HOVER_Z_INDEX = 1;
export const DESTINATION_MARKER_Z_INDEX = 1;
export const DESTINATION_MARKER_HOVER_Z_INDEX = 2;

export function getPoiMarkerZIndex(isHovered: boolean): number {
	return isHovered ? POI_MARKER_HOVER_Z_INDEX : POI_MARKER_Z_INDEX;
}

export function getDestinationMarkerZIndex(isHovered: boolean): number {
	return isHovered ? DESTINATION_MARKER_HOVER_Z_INDEX : DESTINATION_MARKER_Z_INDEX;
}
